// Copyright (c) 2020 WiseTime. All rights reserved.

import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { RootState } from "app/store"
import { TimelineDate } from "../timeline/dateTime"
import { emptyArray } from "../../lib/utils"
import { User } from "generated/wisetime/account/user/v4/user_pb"
import { Membership, SitePermission } from "generated/legebill/site/membership/v4/membership_pb"
import { SiteRef } from "generated/wisetime/type/site_ref_pb"
import { Site } from "../../generated/legebill/site/v4/site_pb"
import { Notification } from "../../generated/wisetime/user/notification/v4/notification_pb"
import { UserTimelineMessage } from "../../generated/wisetime/user/timelinemessage/v4/user_timeline_message_pb"
import { TeamProto } from "../../generated/wt/svc/customer/user_team_pb"
import { Authority, SubscriberState } from "../../generated/wt/svc/shared/shared_pb"
import { DEFAULT_ROUNDING_PREFERENCE_SECONDS } from "../timeline/roundingPreferences"

const setGeneralNotifications = (notification: Notification.AsObject, generalNotifications: Notification.AsObject[]): void => {
  const index = generalNotifications.findIndex(n => n.id === notification.id)
  if (index !== -1) {
    // update existing notification
    generalNotifications[index] = notification
    return
  }
  // add new notification
  generalNotifications.push(notification)
}

const setNotificationsCache = (notification: Notification.AsObject, notificationsCache: NotificationsCache): void => {
  const notificationsCacheBySiteId = notificationsCache[notification.siteId]
  if (notificationsCacheBySiteId === undefined) {
    // add first notification
    notificationsCache[notification.siteId] = [notification]
    return
  }
  const index = notificationsCacheBySiteId.findIndex(n => n.id === notification.id)
  if (index !== -1) {
    // update existing notification
    notificationsCacheBySiteId[index] = notification
    return
  }
  // add new notification
  notificationsCacheBySiteId.push(notification)
}

const removeNotificationFromCache = (notificationId: string, currentSiteId: string, notificationsCache: NotificationsCache): void => {
  const notificationsCacheBySiteId = notificationsCache[currentSiteId]
  if (notificationsCacheBySiteId === undefined) {
    return
  }
  const index = notificationsCacheBySiteId.findIndex(n => n.id === notificationId)
  if (index !== -1) {
    notificationsCacheBySiteId.splice(index, 1)
  }
  if (notificationsCacheBySiteId.length === 0) {
    delete notificationsCache[currentSiteId]
  }
}

export type UserId = string
export type SiteId = string

export type UserDetails = {
  user: User,
  memberships: Membership[],
}

export type SetCurrentSiteIdPayload = {
  userId: UserId,
  siteId: SiteId,
}

export type NotificationsCache = {
  [siteId: string]: Notification.AsObject[],
}

export enum AutoOpenNotificationsPane {
  INITIAL = 1,
  OPENED = 2,
}

type SitePermissionsCache = {
  [siteId: string]: SitePermission[],
}

type SitePermissionsPayload = {
  siteId: string,
  sitePermissionsList: SitePermission[],
}

export type TimelineMessagesCacheKey = string
export const timelineMessagesCacheKey = (date: TimelineDate, siteId: string): TimelineMessagesCacheKey =>
  `${date}-${siteId}`
export type TimelineMessagesCache = {
  [timelineMessagesCacheKey: TimelineMessagesCacheKey]: UserTimelineMessage.AsObject[],
}
export type SetTimelineMessagesPayload = {
  cacheKey: TimelineMessagesCacheKey,
  timelineMessagesList: UserTimelineMessage.AsObject[],
}

export type UserContextState = {
  user: User.AsObject | undefined,
  memberships: Membership.AsObject[],
  currentSiteId: SiteId | undefined,
  currentSite: Site.AsObject | undefined,
  teamProto: TeamProto.AsObject | undefined,
  currentSiteMemberships: Membership.AsObject[],
  generalNotifications: Notification.AsObject[],
  notificationsCache: NotificationsCache,
  autoOpenNotificationsPane: AutoOpenNotificationsPane,
  sidebarIsRendered: boolean,
  sitePermissionsCache: SitePermissionsCache,
  timelineMessagesCache: TimelineMessagesCache,
}

export const initialState: UserContextState = {
  user: undefined,
  memberships: [],
  currentSiteId: undefined,
  currentSite: undefined,
  teamProto: undefined,
  currentSiteMemberships: [],
  generalNotifications: [],
  notificationsCache: {},
  autoOpenNotificationsPane: AutoOpenNotificationsPane.INITIAL,
  sidebarIsRendered: false,
  sitePermissionsCache: {},
  timelineMessagesCache: {},
}

export const slice = createSlice({
  name: "user-context",
  initialState,
  reducers: {
    clearCurrentSiteId: (state): void => {
      state.currentSiteId = undefined
    },
    setCurrentSiteId: (state, action: PayloadAction<SetCurrentSiteIdPayload>): void => {
      state.currentSiteId = action.payload.siteId
    },
    setCurrentSite: (state, action: PayloadAction<Site.AsObject>): void => {
      state.currentSite = action.payload
    },
    setTeamProto: (state, action: PayloadAction<TeamProto.AsObject | undefined>): void => {
      state.teamProto = action.payload
    },
    setUserDetails: (state, action: PayloadAction<UserDetails>): void => {
      state.user = action.payload.user.toObject()
      state.memberships = action.payload.memberships.map(m => m.toObject())
    },
    setNotifications: (state, action: PayloadAction<Notification.AsObject[]>): void => {
      action.payload.forEach((notification: Notification.AsObject) => {
        if (!notification.siteId) {
          setGeneralNotifications(notification, state.generalNotifications)
          return
        }
        setNotificationsCache(notification, state.notificationsCache)
      })
    },
    setAutoOpenNotificationsPaneToOpened: (state): void => {
      state.autoOpenNotificationsPane = AutoOpenNotificationsPane.OPENED
    },
    removeNotification: (state, action: PayloadAction<string>): void => {
      state.generalNotifications = state.generalNotifications.filter(n => n.id !== action.payload)
      removeNotificationFromCache(action.payload, state.currentSiteId ?? "", state.notificationsCache)
    },
    setSidebarIsRendered: (state): void => {
      state.sidebarIsRendered = true
    },
    setSitePermissions: (state, action: PayloadAction<SitePermissionsPayload>): void => {
      state.sitePermissionsCache[action.payload.siteId] = action.payload.sitePermissionsList
    },
    setTimelineMessages: (state, action: PayloadAction<SetTimelineMessagesPayload[]>): void => {
      action.payload.forEach(timelineMessages => {
        if (timelineMessages.timelineMessagesList.length) {
          state.timelineMessagesCache[timelineMessages.cacheKey] = timelineMessages.timelineMessagesList
        } else {
          delete state.timelineMessagesCache[timelineMessages.cacheKey]
        }
      })
    },
  }
})

export const {
  clearCurrentSiteId,
  setCurrentSiteId,
  setCurrentSite,
  setTeamProto,
  setUserDetails,
  setNotifications,
  removeNotification,
  setAutoOpenNotificationsPaneToOpened,
  setSidebarIsRendered,
  setSitePermissions,
  setTimelineMessages,
} = slice.actions

export const selectAreFetchedMemberships = (state: RootState): boolean => state.userContext.user !== undefined
export const selectMemberships = (state: RootState): Membership.AsObject[] => state.userContext.memberships
export const selectCurrentMembershipId = (state: RootState): string | undefined => state.userContext.memberships
  .find(m => m.siteId === state.userContext.currentSiteId)?.id
export const selectCurrentExternalId = (state: RootState): string => state.userContext.memberships
  .find(m => m.siteId === state.userContext.currentSiteId)?.externalId ?? ""
export const selectCurrentSiteId = (state: RootState): SiteId | undefined => state.userContext.currentSiteId
export const selectAllowManageTimesheetFields = (state: RootState) => {
  if (!state.userContext.currentSiteId) {
    return true
  }
  const authority = state.userContext.memberships.find(m => m.siteId === state.userContext.currentSiteId)?.authority ?? Authority.AUTHORITY_UNSPECIFIED
  return [Authority.OWNER, Authority.USER_ADMIN].includes(authority)
}
export const selectTimelineMessagesCache = (state: RootState): TimelineMessagesCache =>
  state.userContext.timelineMessagesCache
export const selectTimelineMessagesCombiner = (date: TimelineDate, currentSiteId: SiteId | undefined, timelineMessagesCache: TimelineMessagesCache) =>
  (timelineMessagesCache[timelineMessagesCacheKey(date, currentSiteId ?? "")] ?? emptyArray)
    .filter(m => m.siteId === currentSiteId)
export const selectTimelineMessages = () => createSelector(
  (_: RootState, date: TimelineDate) => date,
  selectCurrentSiteId,
  selectTimelineMessagesCache,
  selectTimelineMessagesCombiner,
)
export const selectCurrentSiteRef = (state: RootState): SiteRef.AsObject | undefined =>
  state.userContext?.user?.teamSitesList.find(siteRef => siteRef.id === state.userContext.currentSiteId)
export const selectCurrentSite = (state: RootState): Site.AsObject | undefined => state.userContext.currentSite
export const selectSiteRoundingPreferenceSec = (state: RootState): number => {
  const roundingPreferenceSec = state.userContext.currentSite?.roundingPreferenceSec
  if (roundingPreferenceSec === undefined || roundingPreferenceSec <= 0) {
    return DEFAULT_ROUNDING_PREFERENCE_SECONDS
  }

  return roundingPreferenceSec
}
export const selectSitePermissions = (state: RootState): SitePermission[] => (
  state.userContext.sitePermissionsCache[state.userContext.currentSiteId ?? ""] ?? emptyArray
)
export const selectTeamSitesList = (state: RootState): SiteRef.AsObject[] => state.userContext?.user?.teamSitesList ?? emptyArray
export const selectUser = (state: RootState): User.AsObject | undefined => state.userContext.user
export const selectUserEmail = (state: RootState): string => state.userContext.user?.email ?? ""
export const selectNarrateGptEnabled = (state: RootState): boolean =>
  state.userContext.user?.serviceAddOns?.narrateGptEnabled === true
export const selectGeneralNotifications = (state: RootState): Notification.AsObject[] => state.userContext.generalNotifications
export const selectSiteNotifications = (state: RootState): Notification.AsObject[] =>
  state.userContext.notificationsCache[state.userContext.currentSiteId ?? ""] ?? emptyArray
export const selectAutoOpenNotificationsPane = (state: RootState): AutoOpenNotificationsPane => state.userContext.autoOpenNotificationsPane
export const selectSidebarIsRendered = (state: RootState): boolean => state.userContext.sidebarIsRendered
export const selectIsRestrictedTeam = (state: RootState): boolean =>
  state.userContext.teamProto?.subscriberInfo?.state === SubscriberState.RESTRICTED
export const selectIsRestrictedTeamWithNotifications = createSelector(
  selectIsRestrictedTeam,
  selectSiteNotifications,
  (isRestrictedTeam, siteNotifications): boolean => isRestrictedTeam && siteNotifications.length > 0,
)

export default slice.reducer
